import { useRef, type RefObject } from "react";
import { useInView } from "./useInView";

// =============================================================================
// useAnimGate — scroll-gate a section's CSS toolkit animations.
// =============================================================================
// Attach the ref + class to an existing section container:
//
//   const [activityRef, activityGate] = useAnimGate<HTMLDivElement>();
//   <div ref={activityRef} className={`p-3 rounded-lg ${activityGate}`}>
//
// While the section is below the fold it carries `anim-hold`, which pauses
// every .anim-fill / .anim-pop / .anim-radial-in / .anim-card-in inside it
// (index.css, reduced-motion-exempt). The moment it scrolls into view the
// class flips to `anim-go` and the animations run — so bars fill and cards
// pop AS Mako scrolls, in sync with the <CountUp> numbers (which carry their
// own per-element observer and fire at the same scroll moment).
//
// One-shot: sections don't re-hold after revealing. No DOM wrapper — the gate
// rides on the section's existing element, so layout is untouched. Returns a
// tuple (not an object) so callers hold the ref as a plain variable — the
// react-hooks/refs lint rule bans `obj.ref` member access during render.
// =============================================================================

export function useAnimGate<T extends HTMLElement>(): readonly [RefObject<T | null>, string] {
  const ref = useRef<T | null>(null);
  const inView = useInView(ref);
  return [ref, inView ? "anim-go" : "anim-hold"] as const;
}
